import { useMutation, useQueryClient } from "@tanstack/react-query";
import { find, forEach, isEmpty } from "lodash/fp";

import { getQueryKey as getTopicQueryKey } from "@app/analytics/TopicReport/hooks/useTopicReport";
import type { Topic } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { replaceWhere } from "@app/utils/arrays";

import { getQueryKey as getTopicListQueryKey } from "./useTopicReports";

type EditTopicTitleParams = Pick<Topic, "id" | "title">;

const updateTopicInTopicsList = (
  topics: Topic[],
  { id, title }: EditTopicTitleParams
) => {
  if (isEmpty(topics)) return topics;

  const topic = find({ id }, topics);

  if (!topic) return topics;

  const updatedTopic = { ...topic, title };
  return replaceWhere(topics, updatedTopic, (t) => t.id === id);
};

const useEditTopicReport = ({ context }: { context: "list" | "report" }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const refetchTopics = (topicId: Topic["id"]) => {
    queryClient.invalidateQueries({
      queryKey: getTopicQueryKey({ topicId, userId: user?.id }),
    });
    queryClient.invalidateQueries({
      queryKey: getTopicListQueryKey(user?.id),
    });
  };

  const editTopicReportTitleMutation = useMutation({
    mutationFn: ({ id, title }: EditTopicTitleParams) =>
      apiClient.topics.patchTopic(id, { titleOverride: title }),
    onMutate: async ({ id, title }) => {
      if (context === "list") {
        const queryKey = getTopicListQueryKey(user?.id);
        await queryClient.cancelQueries({ queryKey });
        const queriesData =
          queryClient.getQueriesData<Topic[]>({ queryKey, exact: false }) ?? [];
        forEach(([queryKey, queryData = []]) => {
          const updatedData = updateTopicInTopicsList(queryData, {
            id,
            title,
          });
          queryClient.setQueryData(queryKey, updatedData);
        }, queriesData);
      }

      if (context === "report") {
        const queryKey = getTopicQueryKey({ topicId: id, userId: user?.id });
        await queryClient.cancelQueries({ queryKey });
        const topic = queryClient.getQueryData(queryKey);
        if (topic) {
          const updatedTopic = { ...topic, title };
          queryClient.setQueryData(queryKey, updatedTopic);
        }
      }
    },
    onSettled: (data, error, { id }) => refetchTopics(id),
  });

  return {
    editTopicReportTitle: editTopicReportTitleMutation,
  };
};

export default useEditTopicReport;
